// src/App.tsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, BrowserRouter as Router, Routes, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import { ThemeProvider } from "./context/ThemeContext";
import "./i18n/config"; // Import i18n configuration
import "./styles/scrollbar.css"; // Import scrollbar styling

// Pages
import NotificationSystem from "./components/ui/NotificationSystem";
import ForgotPassword from "./pages/auth/ForgotPassword";
import Login from "./pages/auth/Login";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import BranchDashboard from "./pages/dashboard/branch/BranchDashboard";
import BranchLayout from "./pages/dashboard/branch/BranchLayout";
import AgentConfigurationPage from "./pages/dashboard/branch/agent/AgentConfigurationPage";
import AnalyticsPage from "./pages/dashboard/branch/analytics/AnalyticsPage";
import CategoriesPage from "./pages/dashboard/branch/categories/CategoriesPage";
import CategoryDetailPage from "./pages/dashboard/branch/categories/CategoryDetailPage";
import MenuItemDetailPage from "./pages/dashboard/branch/menu-items/detail-page/MenuItemDetailPage";
import MenuItemsPage from "./pages/dashboard/branch/menu-items/MenuItemsPage";
import MenuManagementPage from "./pages/dashboard/branch/menu-management/MenuManagementPage";
import RestaurantSettingsPage from "./pages/dashboard/branch/settings/RestaurantSettingsPage";
import VariantManagementPage from "./pages/dashboard/branch/variants/VariantManagementPage";
import FeedbackPage from "./pages/dashboard/branch/feedback/feedbackPage";
import NotFound from "./pages/not-found/NotFound";
import ReduxProvider from "./store/ReduxProvider";
import { useAppDispatch } from "./store/hooks";
import { getCurrentUser } from "./store/slices/authSlice";

const AppContent = () => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  // Effect to set document direction based on language
  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;

    if (i18n.language === "ar") {
      document.documentElement.classList.add("rtl");
    } else {
      document.documentElement.classList.remove("rtl");
    }
  }, [i18n.language]);

  return (
    <ThemeProvider>
      {/* <AuthProvider> */}
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* Protected dashboard routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route path=":branchId" element={<BranchLayout />}>
                <Route index element={<BranchDashboard />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route
                  path="categories/:categoryId"
                  element={<CategoryDetailPage />}
                />
                <Route path="menu" element={<MenuItemsPage />} />
                <Route path="menu/:itemId" element={<MenuItemDetailPage />} />
                <Route path="menu-management" element={<MenuManagementPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="agent" element={<AgentConfigurationPage />} />
                <Route path="variants" element={<VariantManagementPage />} />
                <Route path="feedback" element={<FeedbackPage />} />
                <Route path="settings" element={<RestaurantSettingsPage />} />
              </Route>
            </Route>
          </Route>

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Global Notification System */}
        <NotificationSystem />
      </Router>
      {/* </AuthProvider> */}
    </ThemeProvider>
  );
};

function App() {
  return (
    <ReduxProvider>
      <AppContent />
    </ReduxProvider>
  );
}

export default App;
